import * as z from 'zod';

export const CreateShippoConfigurationRequest = z.object({
  shippoConfiguration: z
    .object({
      _id: z
        .string()
        .describe('ShippoConfiguration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      domesticServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('DomesticServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'GROUND_ADVANTAGE',
                'PRIORITY_MAIL',
                'PRIORITY_MAIL_EXPRESS',
              ])
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z.enum(['FIXED', 'PERCENTAGE']).optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      internationalServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('InternationalServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'FIRST_CLASS_PACKAGE_INTERNATIONAL',
                'PRIORITY_MAIL_INTERNATIONAL',
                'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
              ])
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z.enum(['FIXED', 'PERCENTAGE']).optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      packageType: z.enum([
        'CUSTOM',
        'FLAT_RATE_ENVELOPE',
        'PADDED_FLAT_RATE_ENVELOPE',
        'SMALL_FLAT_RATE_BOX',
        'MEDIUM_FLAT_RATE_BOX_1',
        'MEDIUM_FLAT_RATE_BOX_2',
        'LARGE_FLAT_RATE_BOX',
      ]),
      packageDetails: z
        .object({
          name: z
            .string()
            .describe(
              'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .max(255)
            .optional()
            .nullable(),
          dimensions: z
            .object({
              length: z.string().describe('Package length.'),
              width: z.string().describe('Package width.'),
              height: z.string().describe('Package height.'),
            })
            .describe(
              'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .optional(),
          maxNumberOfProducts: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
            )
            .optional()
            .nullable(),
        })
        .describe('Package details.'),
      osLocationId: z
        .string()
        .describe('Location ID from OS location service.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      shipFromAddress: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Ship from address materialized from OS location.')
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      backupWeight: z.string().describe('Backup weight.').optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
    })
    .describe('ShippoConfiguration to be created.'),
  options: z
    .object({ backupRate: z.string().optional().nullable() })
    .optional(),
});
export const CreateShippoConfigurationResponse = z.object({
  _id: z
    .string()
    .describe('ShippoConfiguration ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ShippoConfiguration was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ShippoConfiguration was last updated.')
    .optional()
    .nullable(),
  deliveryRegionId: z
    .string()
    .describe('Associated delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  domesticServices: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('DomesticServiceSettings ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        service: z
          .enum(['GROUND_ADVANTAGE', 'PRIORITY_MAIL', 'PRIORITY_MAIL_EXPRESS'])
          .describe('USPS domestic service.')
          .optional(),
        serviceSettings: z
          .object({
            estimatedDeliveryTime: z
              .string()
              .describe('Estimated delivery time.')
              .max(500)
              .optional()
              .nullable(),
            handlingFee: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                  )
                  .optional(),
                calculationType: z
                  .enum(['FIXED', 'PERCENTAGE'])
                  .describe(
                    'How to calculate the fee: fixed amount or by percentage.'
                  )
                  .optional(),
              })
              .describe('Handling fee.')
              .optional(),
            freeDeliveryMinimumAmount: z
              .string()
              .describe('Amount above which free delivery is offered.')
              .optional()
              .nullable(),
          })
          .describe('Service settings.')
          .optional(),
      })
    )
    .max(3)
    .optional(),
  internationalServices: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('InternationalServiceSettings ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        service: z
          .enum([
            'FIRST_CLASS_PACKAGE_INTERNATIONAL',
            'PRIORITY_MAIL_INTERNATIONAL',
            'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
          ])
          .describe('USPS international service.')
          .optional(),
        serviceSettings: z
          .object({
            estimatedDeliveryTime: z
              .string()
              .describe('Estimated delivery time.')
              .max(500)
              .optional()
              .nullable(),
            handlingFee: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                  )
                  .optional(),
                calculationType: z
                  .enum(['FIXED', 'PERCENTAGE'])
                  .describe(
                    'How to calculate the fee: fixed amount or by percentage.'
                  )
                  .optional(),
              })
              .describe('Handling fee.')
              .optional(),
            freeDeliveryMinimumAmount: z
              .string()
              .describe('Amount above which free delivery is offered.')
              .optional()
              .nullable(),
          })
          .describe('Service settings.')
          .optional(),
      })
    )
    .max(3)
    .optional(),
  packageType: z
    .enum([
      'CUSTOM',
      'FLAT_RATE_ENVELOPE',
      'PADDED_FLAT_RATE_ENVELOPE',
      'SMALL_FLAT_RATE_BOX',
      'MEDIUM_FLAT_RATE_BOX_1',
      'MEDIUM_FLAT_RATE_BOX_2',
      'LARGE_FLAT_RATE_BOX',
    ])
    .describe('Package type.')
    .optional(),
  packageDetails: z
    .object({
      name: z
        .string()
        .describe(
          'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
        )
        .max(255)
        .optional()
        .nullable(),
      dimensions: z
        .object({
          length: z.string().describe('Package length.').optional(),
          width: z.string().describe('Package width.').optional(),
          height: z.string().describe('Package height.').optional(),
        })
        .describe(
          'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
        )
        .optional(),
      maxNumberOfProducts: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
        )
        .optional()
        .nullable(),
    })
    .describe('Package details.')
    .optional(),
  osLocationId: z
    .string()
    .describe('Location ID from OS location service.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  shipFromAddress: z
    .object({
      streetAddress: z
        .object({ name: z.string().optional(), number: z.string().optional() })
        .optional(),
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
      addressLine2: z.string().optional().nullable(),
    })
    .describe('Ship from address materialized from OS location.')
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
  backupWeight: z.string().describe('Backup weight.').optional().nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data extensions.')
    .optional(),
});
export const GetShippoConfigurationRequest = z.object({
  shippoConfigurationId: z
    .string()
    .describe('ID of the ShippoConfiguration to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetShippoConfigurationResponse = z.object({
  _id: z
    .string()
    .describe('ShippoConfiguration ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ShippoConfiguration was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ShippoConfiguration was last updated.')
    .optional()
    .nullable(),
  deliveryRegionId: z
    .string()
    .describe('Associated delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  domesticServices: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('DomesticServiceSettings ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        service: z
          .enum(['GROUND_ADVANTAGE', 'PRIORITY_MAIL', 'PRIORITY_MAIL_EXPRESS'])
          .describe('USPS domestic service.')
          .optional(),
        serviceSettings: z
          .object({
            estimatedDeliveryTime: z
              .string()
              .describe('Estimated delivery time.')
              .max(500)
              .optional()
              .nullable(),
            handlingFee: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                  )
                  .optional(),
                calculationType: z
                  .enum(['FIXED', 'PERCENTAGE'])
                  .describe(
                    'How to calculate the fee: fixed amount or by percentage.'
                  )
                  .optional(),
              })
              .describe('Handling fee.')
              .optional(),
            freeDeliveryMinimumAmount: z
              .string()
              .describe('Amount above which free delivery is offered.')
              .optional()
              .nullable(),
          })
          .describe('Service settings.')
          .optional(),
      })
    )
    .max(3)
    .optional(),
  internationalServices: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('InternationalServiceSettings ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        service: z
          .enum([
            'FIRST_CLASS_PACKAGE_INTERNATIONAL',
            'PRIORITY_MAIL_INTERNATIONAL',
            'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
          ])
          .describe('USPS international service.')
          .optional(),
        serviceSettings: z
          .object({
            estimatedDeliveryTime: z
              .string()
              .describe('Estimated delivery time.')
              .max(500)
              .optional()
              .nullable(),
            handlingFee: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                  )
                  .optional(),
                calculationType: z
                  .enum(['FIXED', 'PERCENTAGE'])
                  .describe(
                    'How to calculate the fee: fixed amount or by percentage.'
                  )
                  .optional(),
              })
              .describe('Handling fee.')
              .optional(),
            freeDeliveryMinimumAmount: z
              .string()
              .describe('Amount above which free delivery is offered.')
              .optional()
              .nullable(),
          })
          .describe('Service settings.')
          .optional(),
      })
    )
    .max(3)
    .optional(),
  packageType: z
    .enum([
      'CUSTOM',
      'FLAT_RATE_ENVELOPE',
      'PADDED_FLAT_RATE_ENVELOPE',
      'SMALL_FLAT_RATE_BOX',
      'MEDIUM_FLAT_RATE_BOX_1',
      'MEDIUM_FLAT_RATE_BOX_2',
      'LARGE_FLAT_RATE_BOX',
    ])
    .describe('Package type.')
    .optional(),
  packageDetails: z
    .object({
      name: z
        .string()
        .describe(
          'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
        )
        .max(255)
        .optional()
        .nullable(),
      dimensions: z
        .object({
          length: z.string().describe('Package length.').optional(),
          width: z.string().describe('Package width.').optional(),
          height: z.string().describe('Package height.').optional(),
        })
        .describe(
          'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
        )
        .optional(),
      maxNumberOfProducts: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
        )
        .optional()
        .nullable(),
    })
    .describe('Package details.')
    .optional(),
  osLocationId: z
    .string()
    .describe('Location ID from OS location service.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  shipFromAddress: z
    .object({
      streetAddress: z
        .object({ name: z.string().optional(), number: z.string().optional() })
        .optional(),
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
      addressLine2: z.string().optional().nullable(),
    })
    .describe('Ship from address materialized from OS location.')
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
  backupWeight: z.string().describe('Backup weight.').optional().nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data extensions.')
    .optional(),
});
export const UpdateShippoConfigurationRequest = z.object({
  _id: z
    .string()
    .describe('ShippoConfiguration ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  shippoConfiguration: z
    .object({
      _id: z
        .string()
        .describe('ShippoConfiguration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z.string().regex(/^-?\d+$/, 'Must be a valid Int64 string'),
      _createdDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      domesticServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('DomesticServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'GROUND_ADVANTAGE',
                'PRIORITY_MAIL',
                'PRIORITY_MAIL_EXPRESS',
              ])
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z.enum(['FIXED', 'PERCENTAGE']).optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      internationalServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('InternationalServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'FIRST_CLASS_PACKAGE_INTERNATIONAL',
                'PRIORITY_MAIL_INTERNATIONAL',
                'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
              ])
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z.enum(['FIXED', 'PERCENTAGE']).optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      packageType: z
        .enum([
          'CUSTOM',
          'FLAT_RATE_ENVELOPE',
          'PADDED_FLAT_RATE_ENVELOPE',
          'SMALL_FLAT_RATE_BOX',
          'MEDIUM_FLAT_RATE_BOX_1',
          'MEDIUM_FLAT_RATE_BOX_2',
          'LARGE_FLAT_RATE_BOX',
        ])
        .optional(),
      packageDetails: z
        .object({
          name: z
            .string()
            .describe(
              'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .max(255)
            .optional()
            .nullable(),
          dimensions: z
            .object({
              length: z.string().describe('Package length.').optional(),
              width: z.string().describe('Package width.').optional(),
              height: z.string().describe('Package height.').optional(),
            })
            .describe(
              'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .optional(),
          maxNumberOfProducts: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
            )
            .optional()
            .nullable(),
        })
        .describe('Package details.')
        .optional(),
      osLocationId: z
        .string()
        .describe('Location ID from OS location service.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      shipFromAddress: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Ship from address materialized from OS location.')
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      backupWeight: z.string().describe('Backup weight.').optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
    })
    .describe('ShippoConfiguration to be updated, may be partial.'),
});
export const UpdateShippoConfigurationResponse = z.object({
  _id: z
    .string()
    .describe('ShippoConfiguration ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ShippoConfiguration was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ShippoConfiguration was last updated.')
    .optional()
    .nullable(),
  deliveryRegionId: z
    .string()
    .describe('Associated delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  domesticServices: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('DomesticServiceSettings ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        service: z
          .enum(['GROUND_ADVANTAGE', 'PRIORITY_MAIL', 'PRIORITY_MAIL_EXPRESS'])
          .describe('USPS domestic service.')
          .optional(),
        serviceSettings: z
          .object({
            estimatedDeliveryTime: z
              .string()
              .describe('Estimated delivery time.')
              .max(500)
              .optional()
              .nullable(),
            handlingFee: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                  )
                  .optional(),
                calculationType: z
                  .enum(['FIXED', 'PERCENTAGE'])
                  .describe(
                    'How to calculate the fee: fixed amount or by percentage.'
                  )
                  .optional(),
              })
              .describe('Handling fee.')
              .optional(),
            freeDeliveryMinimumAmount: z
              .string()
              .describe('Amount above which free delivery is offered.')
              .optional()
              .nullable(),
          })
          .describe('Service settings.')
          .optional(),
      })
    )
    .max(3)
    .optional(),
  internationalServices: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('InternationalServiceSettings ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        service: z
          .enum([
            'FIRST_CLASS_PACKAGE_INTERNATIONAL',
            'PRIORITY_MAIL_INTERNATIONAL',
            'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
          ])
          .describe('USPS international service.')
          .optional(),
        serviceSettings: z
          .object({
            estimatedDeliveryTime: z
              .string()
              .describe('Estimated delivery time.')
              .max(500)
              .optional()
              .nullable(),
            handlingFee: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                  )
                  .optional(),
                calculationType: z
                  .enum(['FIXED', 'PERCENTAGE'])
                  .describe(
                    'How to calculate the fee: fixed amount or by percentage.'
                  )
                  .optional(),
              })
              .describe('Handling fee.')
              .optional(),
            freeDeliveryMinimumAmount: z
              .string()
              .describe('Amount above which free delivery is offered.')
              .optional()
              .nullable(),
          })
          .describe('Service settings.')
          .optional(),
      })
    )
    .max(3)
    .optional(),
  packageType: z
    .enum([
      'CUSTOM',
      'FLAT_RATE_ENVELOPE',
      'PADDED_FLAT_RATE_ENVELOPE',
      'SMALL_FLAT_RATE_BOX',
      'MEDIUM_FLAT_RATE_BOX_1',
      'MEDIUM_FLAT_RATE_BOX_2',
      'LARGE_FLAT_RATE_BOX',
    ])
    .describe('Package type.')
    .optional(),
  packageDetails: z
    .object({
      name: z
        .string()
        .describe(
          'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
        )
        .max(255)
        .optional()
        .nullable(),
      dimensions: z
        .object({
          length: z.string().describe('Package length.').optional(),
          width: z.string().describe('Package width.').optional(),
          height: z.string().describe('Package height.').optional(),
        })
        .describe(
          'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
        )
        .optional(),
      maxNumberOfProducts: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
        )
        .optional()
        .nullable(),
    })
    .describe('Package details.')
    .optional(),
  osLocationId: z
    .string()
    .describe('Location ID from OS location service.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  shipFromAddress: z
    .object({
      streetAddress: z
        .object({ name: z.string().optional(), number: z.string().optional() })
        .optional(),
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
      addressLine2: z.string().optional().nullable(),
    })
    .describe('Ship from address materialized from OS location.')
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
  backupWeight: z.string().describe('Backup weight.').optional().nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data extensions.')
    .optional(),
});
export const DeleteShippoConfigurationRequest = z.object({
  shippoConfigurationId: z
    .string()
    .describe('ID of the ShippoConfiguration to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteShippoConfigurationResponse = z.object({});
export const QueryShippoConfigurationsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          deliveryRegionId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z.enum(['_id', 'deliveryRegionId']).optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('WQL expression.'),
});
export const QueryShippoConfigurationsResponse = z.object({
  shippoConfigurations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('ShippoConfiguration ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the ShippoConfiguration was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the ShippoConfiguration was last updated.')
          .optional()
          .nullable(),
        deliveryRegionId: z
          .string()
          .describe('Associated delivery region ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        domesticServices: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('DomesticServiceSettings ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              service: z
                .enum([
                  'GROUND_ADVANTAGE',
                  'PRIORITY_MAIL',
                  'PRIORITY_MAIL_EXPRESS',
                ])
                .describe('USPS domestic service.')
                .optional(),
              serviceSettings: z
                .object({
                  estimatedDeliveryTime: z
                    .string()
                    .describe('Estimated delivery time.')
                    .max(500)
                    .optional()
                    .nullable(),
                  handlingFee: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                        )
                        .optional(),
                      calculationType: z
                        .enum(['FIXED', 'PERCENTAGE'])
                        .describe(
                          'How to calculate the fee: fixed amount or by percentage.'
                        )
                        .optional(),
                    })
                    .describe('Handling fee.')
                    .optional(),
                  freeDeliveryMinimumAmount: z
                    .string()
                    .describe('Amount above which free delivery is offered.')
                    .optional()
                    .nullable(),
                })
                .describe('Service settings.')
                .optional(),
            })
          )
          .max(3)
          .optional(),
        internationalServices: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('InternationalServiceSettings ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              service: z
                .enum([
                  'FIRST_CLASS_PACKAGE_INTERNATIONAL',
                  'PRIORITY_MAIL_INTERNATIONAL',
                  'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
                ])
                .describe('USPS international service.')
                .optional(),
              serviceSettings: z
                .object({
                  estimatedDeliveryTime: z
                    .string()
                    .describe('Estimated delivery time.')
                    .max(500)
                    .optional()
                    .nullable(),
                  handlingFee: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                        )
                        .optional(),
                      calculationType: z
                        .enum(['FIXED', 'PERCENTAGE'])
                        .describe(
                          'How to calculate the fee: fixed amount or by percentage.'
                        )
                        .optional(),
                    })
                    .describe('Handling fee.')
                    .optional(),
                  freeDeliveryMinimumAmount: z
                    .string()
                    .describe('Amount above which free delivery is offered.')
                    .optional()
                    .nullable(),
                })
                .describe('Service settings.')
                .optional(),
            })
          )
          .max(3)
          .optional(),
        packageType: z
          .enum([
            'CUSTOM',
            'FLAT_RATE_ENVELOPE',
            'PADDED_FLAT_RATE_ENVELOPE',
            'SMALL_FLAT_RATE_BOX',
            'MEDIUM_FLAT_RATE_BOX_1',
            'MEDIUM_FLAT_RATE_BOX_2',
            'LARGE_FLAT_RATE_BOX',
          ])
          .describe('Package type.')
          .optional(),
        packageDetails: z
          .object({
            name: z
              .string()
              .describe(
                'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
              )
              .max(255)
              .optional()
              .nullable(),
            dimensions: z
              .object({
                length: z.string().describe('Package length.').optional(),
                width: z.string().describe('Package width.').optional(),
                height: z.string().describe('Package height.').optional(),
              })
              .describe(
                'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
              )
              .optional(),
            maxNumberOfProducts: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
              )
              .optional()
              .nullable(),
          })
          .describe('Package details.')
          .optional(),
        osLocationId: z
          .string()
          .describe('Location ID from OS location service.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        shipFromAddress: z
          .object({
            streetAddress: z
              .object({
                name: z.string().optional(),
                number: z.string().optional(),
              })
              .optional(),
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
            addressLine2: z.string().optional().nullable(),
          })
          .describe('Ship from address materialized from OS location.')
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
        backupWeight: z
          .string()
          .describe('Backup weight.')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data extensions.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = z.object({
  shippoConfiguration: z
    .object({
      _id: z
        .string()
        .describe('ShippoConfiguration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      domesticServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('DomesticServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'GROUND_ADVANTAGE',
                'PRIORITY_MAIL',
                'PRIORITY_MAIL_EXPRESS',
              ])
              .describe('USPS domestic service.')
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z
                      .enum(['FIXED', 'PERCENTAGE'])
                      .describe(
                        'How to calculate the fee: fixed amount or by percentage.'
                      )
                      .optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      internationalServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('InternationalServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'FIRST_CLASS_PACKAGE_INTERNATIONAL',
                'PRIORITY_MAIL_INTERNATIONAL',
                'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
              ])
              .describe('USPS international service.')
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z
                      .enum(['FIXED', 'PERCENTAGE'])
                      .describe(
                        'How to calculate the fee: fixed amount or by percentage.'
                      )
                      .optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      packageType: z
        .enum([
          'CUSTOM',
          'FLAT_RATE_ENVELOPE',
          'PADDED_FLAT_RATE_ENVELOPE',
          'SMALL_FLAT_RATE_BOX',
          'MEDIUM_FLAT_RATE_BOX_1',
          'MEDIUM_FLAT_RATE_BOX_2',
          'LARGE_FLAT_RATE_BOX',
        ])
        .describe('Package type.')
        .optional(),
      packageDetails: z
        .object({
          name: z
            .string()
            .describe(
              'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .max(255)
            .optional()
            .nullable(),
          dimensions: z
            .object({
              length: z.string().describe('Package length.').optional(),
              width: z.string().describe('Package width.').optional(),
              height: z.string().describe('Package height.').optional(),
            })
            .describe(
              'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .optional(),
          maxNumberOfProducts: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
            )
            .optional()
            .nullable(),
        })
        .describe('Package details.')
        .optional(),
      osLocationId: z
        .string()
        .describe('Location ID from OS location service.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      shipFromAddress: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Ship from address materialized from OS location.')
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      backupWeight: z.string().describe('Backup weight.').optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
    })
    .describe('Updated ShippoConfiguration.')
    .optional(),
});
export const AddDeliveryRegionRequest = z.object({
  shippoConfigurationId: z
    .string()
    .describe('ID of the ShippoConfiguration to add the delivery region to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryRegionId: z
    .string()
    .describe('ID of the delivery region to add.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Revision of the ShippoConfiguration.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const AddDeliveryRegionResponse = z.object({
  shippoConfiguration: z
    .object({
      _id: z
        .string()
        .describe('ShippoConfiguration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      domesticServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('DomesticServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'GROUND_ADVANTAGE',
                'PRIORITY_MAIL',
                'PRIORITY_MAIL_EXPRESS',
              ])
              .describe('USPS domestic service.')
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z
                      .enum(['FIXED', 'PERCENTAGE'])
                      .describe(
                        'How to calculate the fee: fixed amount or by percentage.'
                      )
                      .optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      internationalServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('InternationalServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'FIRST_CLASS_PACKAGE_INTERNATIONAL',
                'PRIORITY_MAIL_INTERNATIONAL',
                'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
              ])
              .describe('USPS international service.')
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z
                      .enum(['FIXED', 'PERCENTAGE'])
                      .describe(
                        'How to calculate the fee: fixed amount or by percentage.'
                      )
                      .optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      packageType: z
        .enum([
          'CUSTOM',
          'FLAT_RATE_ENVELOPE',
          'PADDED_FLAT_RATE_ENVELOPE',
          'SMALL_FLAT_RATE_BOX',
          'MEDIUM_FLAT_RATE_BOX_1',
          'MEDIUM_FLAT_RATE_BOX_2',
          'LARGE_FLAT_RATE_BOX',
        ])
        .describe('Package type.')
        .optional(),
      packageDetails: z
        .object({
          name: z
            .string()
            .describe(
              'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .max(255)
            .optional()
            .nullable(),
          dimensions: z
            .object({
              length: z.string().describe('Package length.').optional(),
              width: z.string().describe('Package width.').optional(),
              height: z.string().describe('Package height.').optional(),
            })
            .describe(
              'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .optional(),
          maxNumberOfProducts: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
            )
            .optional()
            .nullable(),
        })
        .describe('Package details.')
        .optional(),
      osLocationId: z
        .string()
        .describe('Location ID from OS location service.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      shipFromAddress: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Ship from address materialized from OS location.')
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      backupWeight: z.string().describe('Backup weight.').optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
    })
    .describe('The updated ShippoConfiguration.')
    .optional(),
});
export const RemoveDeliveryRegionRequest = z.object({
  identifiers: z.object({
    shippoConfigurationId: z
      .string()
      .describe(
        'ID of the ShippoConfiguration to remove the delivery region from.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    deliveryRegionId: z
      .string()
      .describe('ID of the delivery region to remove.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Revision of the ShippoConfiguration.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const RemoveDeliveryRegionResponse = z.object({
  shippoConfiguration: z
    .object({
      _id: z
        .string()
        .describe('ShippoConfiguration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippoConfiguration was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      domesticServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('DomesticServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'GROUND_ADVANTAGE',
                'PRIORITY_MAIL',
                'PRIORITY_MAIL_EXPRESS',
              ])
              .describe('USPS domestic service.')
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z
                      .enum(['FIXED', 'PERCENTAGE'])
                      .describe(
                        'How to calculate the fee: fixed amount or by percentage.'
                      )
                      .optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      internationalServices: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('InternationalServiceSettings ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            service: z
              .enum([
                'FIRST_CLASS_PACKAGE_INTERNATIONAL',
                'PRIORITY_MAIL_INTERNATIONAL',
                'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
              ])
              .describe('USPS international service.')
              .optional(),
            serviceSettings: z
              .object({
                estimatedDeliveryTime: z
                  .string()
                  .describe('Estimated delivery time.')
                  .max(500)
                  .optional()
                  .nullable(),
                handlingFee: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.'
                      )
                      .optional(),
                    calculationType: z
                      .enum(['FIXED', 'PERCENTAGE'])
                      .describe(
                        'How to calculate the fee: fixed amount or by percentage.'
                      )
                      .optional(),
                  })
                  .describe('Handling fee.')
                  .optional(),
                freeDeliveryMinimumAmount: z
                  .string()
                  .describe('Amount above which free delivery is offered.')
                  .optional()
                  .nullable(),
              })
              .describe('Service settings.')
              .optional(),
          })
        )
        .max(3)
        .optional(),
      packageType: z
        .enum([
          'CUSTOM',
          'FLAT_RATE_ENVELOPE',
          'PADDED_FLAT_RATE_ENVELOPE',
          'SMALL_FLAT_RATE_BOX',
          'MEDIUM_FLAT_RATE_BOX_1',
          'MEDIUM_FLAT_RATE_BOX_2',
          'LARGE_FLAT_RATE_BOX',
        ])
        .describe('Package type.')
        .optional(),
      packageDetails: z
        .object({
          name: z
            .string()
            .describe(
              'Package name. Must be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .max(255)
            .optional()
            .nullable(),
          dimensions: z
            .object({
              length: z.string().describe('Package length.').optional(),
              width: z.string().describe('Package width.').optional(),
              height: z.string().describe('Package height.').optional(),
            })
            .describe(
              'Package dimensions. Can only be set when package type is CUSTOM.\nFor other package types, it is read-only, and an exception will be thrown\nif it set when creating a ShippoConfiguration.'
            )
            .optional(),
          maxNumberOfProducts: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Maximum number of products. Must be set when package type is NOT CUSTOM.\nOtherwise, if the package type is CUSTOM, this field cannot be set and an\nexception will be thrown if it is set when creating a ShippoConfiguration.'
            )
            .optional()
            .nullable(),
        })
        .describe('Package details.')
        .optional(),
      osLocationId: z
        .string()
        .describe('Location ID from OS location service.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      shipFromAddress: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
          addressLine2: z.string().optional().nullable(),
        })
        .describe('Ship from address materialized from OS location.')
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      backupWeight: z.string().describe('Backup weight.').optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data extensions.')
        .optional(),
    })
    .describe(
      'The updated ShippoConfiguration, or empty if the ShippoConfiguration was deleted.'
    )
    .optional(),
});
